import { TableViewStore, BaseListViewStore } from "core/stores";
import { PickTicketDetailService, PickTicketHeaderService } from "application/pick-ticket/services";
import { FacilityOrderTemplateService, FacilityOrderStatusService, FacilityOrderService } from "application/facility-order/services";
import { FacilityOrderPatientService } from "application/facility-order-new-flow-insurance/services";
import { LookupService } from "common/services";
import { ScheduleService } from 'application/schedule/services';
import { action, observable } from "mobx";
import { resolveColumns } from "application/facility/util/resolveColumns";
import { ModalParams } from "core/models";
import React from "react";
import _ from 'lodash';

class PickTicketNewFlowDetailListViewStore extends BaseListViewStore {
    @observable accessor pickTicketHeader = null;
    @observable accessor deliveryDate = null;
    @observable accessor initPageLoad = true;
    @observable accessor sendOrderLoading = false;
    @observable accessor sendingAgoraPo = false;
    @observable accessor isOrderSent = false;
    @observable accessor isImportDisabled = false;
    @observable accessor facilityOrderStatus;
    @observable accessor isImportButtonVisible = false;
    @observable accessor disabledSendOrder = false;

    constructor(rootStore) {
        const pickTicketDetailService = new PickTicketDetailService(rootStore.application.baasic.apiClient);
        const pickTicketHeaderService = new PickTicketHeaderService(rootStore.application.baasic.apiClient);
        const scheduleService = new ScheduleService(rootStore.application.baasic.apiClient);

        const pickTicketHeaderId = rootStore.routerStore.routerState.params.pickTicketHeaderId;

        super(rootStore, {
            autoInit: true,
            name: 'pick-ticket-new-flow-detail-list',
            routes: {},
            actions: () => {
                return {
                    find: async params => {
                        await this.getLookups();

                        if (this.initPageLoad) {
                            await this.getPickTicketHeader(pickTicketHeaderId);
                            this.initPageLoad = false;
                        }

                        await this.checkIsOrderSent();

                        const response = await pickTicketDetailService.find({
                            ...params,
                            pickTicketHeaderId,
                            embed: 'Product,FacilityOrderTemplateItems'
                        });

                        const res = _.extend(response.data, {
                            item: _.map(response.data.item, (item => {
                                return _.extend(
                                    item, {
                                    uom: item.product && item.product.unitOfMeasure && item.product.unitOfMeasure.abrv
                                }
                                );
                            }))
                        })
                        return res;
                    }
                };
            },
        });

        this.isAdmin = rootStore.userStore.user.roles.includes("Administrators");
        this.pickTicketDetailService = pickTicketDetailService;
        this.pickTicketHeaderService = pickTicketHeaderService;
        this.scheduleService = scheduleService;
        this.pickTicketHeaderId = pickTicketHeaderId;
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.facilityOrderPatientService = new FacilityOrderPatientService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(this.rootStore.application.baasic.apiClient);

        this.facilityOrderNewFlowNonScheduledTubesModal = new ModalParams({});
        this.backOrderEditModal = new ModalParams({});
        this.pickTicketInfoModal = new ModalParams({});
        this.pickTicketDetailNewFlowItemDetailsModal = new ModalParams({ onClose: () => this.tableStore.updateDataItems() });
        this.pickTicketRefreshConfirmationModal = new ModalParams({});

        this.pickTicketCreateInProgresConfirmationModal = new ModalParams({ onClose: null });
        this.pickTicketCreateInProgresConfirmationModal.onConfirm = async () => { await this.setPickTicketCreateInProgress(); this.pickTicketCreateInProgresConfirmationModal.close(); };
        this.pickTicketCreateInProgresConfirmationModal.onCancel = () => { this.pickTicketCreateInProgresConfirmationModal.close() };

        this.importConfirmModal = new ModalParams({ onClose: null });
        this.importConfirmModal.onConfirm = async () => { this.importConfirmModal.close(); await this.startImportFlow(); };
        this.importConfirmModal.onCancel = () => { this.importConfirmModal.close() };
        this.importConfirmModal.yesLabel = 'Yes';
        this.importConfirmModal.noLabel = 'No';

        this.primaryRecipientErrorModal = new ModalParams({ onClose: null });
        this.primaryRecipientErrorModal.onCancel = () => { this.primaryRecipientErrorModal.close() };

        const userRoles = this.rootStore.userStore.user.roles;
        this.isImportButtonVisible = userRoles.includes("Administrators") || userRoles.includes("Biller");

        this.setTableStore(
            new TableViewStore(this.queryUtility, {
                columns: resolveColumns(
                    [
                        {
                            key: 'itemId',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.ITEM_ID",
                        },
                        {
                            key: 'itemName',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.ITEM_NAME",
                            cell: (item) => this.renderItemNameCell(item),
                            width: 400
                        },
                        {
                            key: 'category',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.CATEGORY"
                        },
                        {
                            key: 'uom',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.UOM"
                        },
                        {
                            key: 'totalQty',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.QTY",
                            cell: (item) => this.renderTotalQtyCell(item),
                            width: 100
                        },
                        {
                            key: 'backOrderNote',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.BACK_ORDER_NOTE",
                        }
                    ]
                ),
                actions: {
                    onPreview: (item) => { this.openPickTicketDetailNewFlowItemDetailsModal(item) },
                    onEdit: (item) => { this.openBackOrderEditModal(item.id) }
                }
            })
        );
    }

    @action.bound
    async getLookups() {
        const result = await this.lookupService.getLookups('FacilityScheduleType,PickTicketHeaderStatus,ItemCategory,ImportHeaderStatus,AreaOfCare');
        this.pickTicketHeaderStatus = result.pickTicketHeaderStatus;
        this.facilityScheduleType = result.facilityScheduleType;
        this.enteralItemCategoryId = _.find(result.itemCategory, s => { return s.abrv === 'enteral'; }).id;
        this.importHeaderStatus = result.importHeaderStatus;
        this.woundCareAreaOfCareId = _.find(result.areaOfCare, s => { return s.abrv === 'wound-care'; }).id;
    }

    renderTotalQtyCell(item) {
        return (
            <td>
                <div className="u-flex--space-between">
                    {item.dataItem.totalQty}
                    {(item.dataItem.sendSeparatelyQty | 0) > 0 &&
                        <i
                            style={{
                                display: "inline-block",
                                marginLeft: "7px",
                                ariaLabel: "Label"
                            }}
                            className="u-icon u-icon--ss u-icon--sml"
                        />
                    }
                </div>
            </td>
        );
    }

    renderItemNameCell(item) {
        return (
            <td>
                <div className="u-flex--space-between">
                    {item.dataItem.itemName}
                    {(item.dataItem.backordered != null ? item.dataItem.backordered : item.dataItem.product && item.dataItem.product.backordered) &&
                        <i
                            style={{
                                display: "inline-block",
                                marginLeft: "7px",
                                ariaLabel: "Label"
                            }}
                            className="u-icon u-icon--med-lrg u-icon--backordered"
                        />
                    }
                </div>
            </td>
        );
    }

    @action.bound
    async sendOrderNewFlow() {
        try {
            this.sendOrderLoading = true;
            await this.scheduleService.sendOrderNewFlow({ pickTicketHeaderId: this.pickTicketHeaderId });
            await this.checkIsOrderSent();
            this.rootStore.notificationStore.success("Facility Schedule Date successfully updated.");
            this.sendOrderLoading = false;
            await this.pickTicketHeaderService.sendInfoEmail(this.facilityOrderId);
            this.rootStore.notificationStore.success("Pick Ticket email successfully sent.");
        } catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to send Pick Ticket email.", err);
        }
    }

    async checkIsOrderSent() {
        let taskParams = {
            pageSize: 1,
            calendarTypeId: _.find(this.facilityScheduleType, s => { return s.abrv === 'ordering'; }).id,
            facilityOrderId: this.facilityOrderId,
            fields: "sendOrder",
            areaOfCareIdToExclude: this.woundCareAreaOfCareId,
        }
        const tasks = await this.scheduleService.find(taskParams);
        if (tasks.data && tasks.data.item) {
            this.isOrderSent = tasks.data.item.some(i => i.sendOrder);
        }
    }

    @action.bound
    async refreshNewFlow() {
        if (this.pickTicketHeader.pickTicketHeaderStatus && this.pickTicketHeader.pickTicketHeaderStatus.abrv === 'new') {
            this.rootStore.modalStore.showConfirm(
                'PICK_TICKET_DETAIL.REFRESH.CONFIRM_REFRESH',
                async () => {
                    await this.refreshPickTicketNewFlow();
                }
            );
        }
        else {
            this.pickTicketRefreshConfirmationModal.open({
                isInProgres: this.pickTicketHeader.pickTicketHeaderStatus && this.pickTicketHeader.pickTicketHeaderStatus.abrv === 'in-progress',
                hasBackOrder: this.tableStore.originalData.some(i => i.backOrder) || this.tableStore.originalData.some(i => i.backOrderNote),
                refreshPickTicket: () => this.refreshPickTicketNewFlow()
            });
        }
    }

    @action.bound
    async refreshPickTicketNewFlow() {
        try {
            await this.pickTicketHeaderService.refreshNewFlow(this.pickTicketHeader.id);
            this.rootStore.notificationStore.success('PICK_TICKET_DETAIL.REFRESH.SUCCESS_REFRESH');
            await this.queryUtility.fetch();
        }
        catch (err) {
            if (err.data == "Pick Ticker refresh is already in progress.") {
                this.showPickTicketCreateInProgresModal();
            }
            else {
                this.rootStore.notificationStore.error(err.data ? err.data : 'PICK_TICKET_DETAIL.REFRESH.ERROR_REFRESH', err);
            }
        }

        if (this.pickTicketInfoModal.isOpen) {
            this.pickTicketInfoModal.close()
        }
    }

    async getPickTicketHeader(pickTicketHeaderId) {
        const response = await this.pickTicketHeaderService.get(pickTicketHeaderId, {
            embed: "facilityOrders,pickTicketHeaderStatus"
        });

        this.pickTicketHeader = response.data;
        const facilityName = this.pickTicketHeader && this.pickTicketHeader.facilityOrders[0].facility.name;
        this.rootStore.titleStore.setTitle(facilityName ? `PT - ${facilityName}` : 'Pick Ticket');

        this.facilityOrderId = response.data && response.data.facilityOrders[0].id;
        this.facilityOrder = (await this.facilityOrderService.get(this.facilityOrderId, { embed: "Facility,Facility.FacilityAreaOfCares,FacilityOrderStatuses" })).data;
        this.facilityOrderStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'send-order');
        this.disabledSendOrder = !this.isAdmin && this.facilityOrder && this.facilityOrder.sendOrder;
        this.primaryRecipientErrorModal.facilityOrderId = this.facilityOrderId;

        await this.getDeliveryDate(response.data && response.data.facilityOrders[0].id)

        if (localStorage.getItem('refreshPickTicket')) {
            localStorage.removeItem('refreshPickTicket');
            try {
                if (this.pickTicketHeader.pickTicketHeaderStatus && this.pickTicketHeader.pickTicketHeaderStatus.abrv === 'new') {
                    await this.pickTicketHeaderService.refreshNewFlow(this.pickTicketHeader.id);
                    if (localStorage.getItem(`updatePickTicketStatus${this.pickTicketHeader.id}`)) {
                        localStorage.removeItem(`updatePickTicketStatus${this.pickTicketHeader.id}`);

                        await this.pickTicketHeaderService.setStatus({ id: this.pickTicketHeader.id, pickTicketHeaderStatusId: this.pickTicketHeaderStatus.find(x => x.abrv === 'in-progress').id });
                        this.pickTicketHeader.pickTicketHeaderStatus = this.pickTicketHeaderStatus.find(x => x.abrv === 'in-progress');
                    }
                }
            }
            catch (err) {
                if (localStorage.getItem(`updatePickTicketStatus${this.pickTicketHeader.id}`)) {
                    localStorage.removeItem(`updatePickTicketStatus${this.pickTicketHeader.id}`);
                }
                if (err.data == "Pick Ticker refresh is already in progress.") {
                    this.showPickTicketCreateInProgresModal();
                }
                else {
                    this.rootStore.notificationStore.error(err && err.data ? err.data : 'PICK_TICKET_DETAIL.REFRESH.ERROR_REFRESH');
                }
            }
        }

        const includedExcludedItems = (await this.pickTicketHeaderService.getExcludedAndIncludedFacilityOrderTemplateItems(this.facilityOrderId)).data.item;
        if (includedExcludedItems.some(i => i))
            this.openPickTicketInfoModal();
    }

    @action.bound
    async getDeliveryDate(facilityOrderId) {
        const typeResponse = await this.scheduleService.getTypes();
        let orderType = typeResponse.item.find(i => i.abrv === 'delivery');
        const response = await this.scheduleService.find({ facilityOrderId: facilityOrderId, calendarTypeId: orderType && orderType.id });
        this.deliveryDate = response.data && response.data.item[0] && response.data.item[0].date;
    }

    openBackOrderEditModal(pickTicketDetailId) {
        this.backOrderEditModal.open({
            pickTicketDetailId,
            fetchTable: this.queryUtility.fetch
        });
    }

    @action.bound
    async downloadReport(name) {
        if (name === "Agora PO") {
            this.downloadAgoraPo();
        }
        else if (name === 'Pick Ticket XLSX') {
            this.downloadPickTicket('XLSX');
        } else if (name === 'Pick Ticket PDF') {
            this.downloadPickTicket('PDF');
        } else if (name === 'FO Report') {
            this.downloadFacilityOrder();
        }
    }

    @action.bound
    async refreshPickTicket() {
        if (this.pickTicketHeader.pickTicketHeaderStatus && this.pickTicketHeader.pickTicketHeaderStatus.abrv === 'new') {
            try {
                await this.pickTicketHeaderService.refreshNewFlow(this.pickTicketHeaderId);
            }
            catch (err) {
                this.rootStore.notificationStore.error(err.data ? err.data : 'PICK_TICKET_DETAIL.REFRESH.ERROR_REFRESH', err);
                return false;
            }
        }
        return true;
    }

    @action.bound
    async downloadPickTicket(format) {
        if (await this.refreshPickTicket()) {
            const pickTicketReportDownloadUrl = `${this.pickTicketHeaderService.getPickTicketReportDownloadUrlNewFlow(this.pickTicketHeaderId)}?format=${format}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
            window.open(pickTicketReportDownloadUrl);
        }
    }

    @action.bound
    async sendAgoraPo() {
        var result = (await this.facilityOrderTemplateService.importCheck(this.facilityOrder.id, "Send Agora PO")).data;
        if (!result.success) {
            return this.rootStore.notificationStore.error(result.errors);
        }
        this.sendingAgoraPo = true;
        try {
            await this.pickTicketHeaderService.sendAgoraPo(this.pickTicketHeaderId);
            this.rootStore.notificationStore.success("Agora PO successfully sent.");
        }
        catch (err) {
            return err.data.errors == "There are no items to include on the Agora PO." ? this.rootStore.notificationStore.success(err.data.errors) : this.rootStore.notificationStore.error("Unable to send Agora PO!");
        }
        finally {
            this.sendingAgoraPo = false;
        }
    }

    @action.bound
    async downloadFacilityOrder() {
        const downloadUrl = `${(await this.facilityOrderTemplateService.getDownloadNewFlowUrl({ ...this.queryUtility.plainFilter, facilityOrderId: this.facilityOrder.id }))}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl
            }&ttype=${this.rootStore.authStore.token.type}`
        window.open(downloadUrl, "_blank")
    }

    @action.bound
    async openPickTicketInfoModal() {
        this.pickTicketInfoModal.open({
            facilityOrderId: this.facilityOrderId,
            refreshPickTicket: () => this.refreshNewFlow(),
            disabledSendOrder: this.disabledSendOrder
        });
    }

    @action.bound
    openPickTicketDetailNewFlowItemDetailsModal(item) {
        this.pickTicketDetailNewFlowItemDetailsModal.open({
            facilityOrder: this.facilityOrder,
            pickTicketDetail: item,
            pickTicketHeader: this.pickTicketHeader
        });
    }

    @action.bound
    async showPickTicketCreateInProgresModal() {
        this.pickTicketCreateInProgresConfirmationModal.open({
            message: "A pick ticket refresh is already in progress. Completing the refresh now may override ongoing changes. Would you like to proceed and finish the pick ticket refresh?"
        });
    }

    @action.bound
    async setPickTicketCreateInProgress() {
        let pickTicketHeader = (await this.pickTicketHeaderService.get(this.pickTicketHeaderId)).data;
        if (pickTicketHeader.pickTicketCreateInProgress) {
            pickTicketHeader.pickTicketCreateInProgress = false;
            await this.pickTicketHeaderService.update(pickTicketHeader);
        }
    }

    @action.bound
    signalRAction(message) {
        if (message.table == "PTH" || message.column == "SendOrder") {
            this.setPickTicketHeaderStatus();
        }
        else if (message.table == "PT-Refresh") {
            this.queryUtility.fetch();
        }
    }

    @action.bound
    async setPickTicketHeaderStatus() {
        try {
            const response = await this.pickTicketHeaderService.get(this.pickTicketHeaderId, { embed: "facilityOrders,pickTicketHeaderStatus" });
            this.pickTicketHeader = response.data
            this.facilityOrder = (await this.facilityOrderService.get(this.facilityOrderId, { embed: "Facility,Facility.FacilityAreaOfCares,FacilityOrderStatuses" })).data;
            this.facilityOrderStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'send-order');
            this.disabledSendOrder = !this.isAdmin && this.facilityOrder && this.facilityOrder.sendOrder;
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
    }

    @action.bound
    async showImportConfirmModal() {
        var result = (await this.facilityOrderTemplateService.importCheck(this.facilityOrder.id, "Import to BT")).data;
        if (!result.success) {
            return this.rootStore.notificationStore.error(result.errors);
        }

        await this.importConfirmModal.open({
            message: 'FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_IMPORT'
        });
    }

    @action.bound
    async startImportFlow() {
        this.isImportDisabled = true;
        try {
            const status = this.importHeaderStatus.find(i => i.abrv === 'send-order');
            const facilityOrderStatusService = new FacilityOrderStatusService(this.rootStore.application.baasic.apiClient);
            await facilityOrderStatusService.create({ facilityOrderId: this.facilityOrderId, statusId: status.id, startedOn: new Date(), startedByUserId: this.rootStore.userStore.user.id, completedOn: new Date(), completedByUserId: this.rootStore.userStore.user.id });
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
            this.isImportDisabled = false;
            return;
        }
        await this.setPickTicketHeaderStatus();
        this.rootStore.notificationStore.success("Importing template(s) to Brightree is in progress.");
        await this.importFacilityOrder();
        this.isImportDisabled = false;
    }

    async importFacilityOrder() {
        try {
            await this.facilityOrderService.importFacilityOrderNewFlow({ facilityOrderId: this.facilityOrder.id });
        }
        catch (err) {
            err.data.errors == "There are no templates to import to BT." ? this.rootStore.notificationStore.success(err.data.errors) : this.rootStore.notificationStore.error(err.data.errors);
        }
    }

    @action.bound
    async downloadAgoraPo() {
        if (await this.refreshPickTicket()) {
            const agoraPoReportDownloadUrl = `${this.pickTicketHeaderService.getAgoraPoReportDownloadUrl(this.pickTicketHeaderId)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
            window.open(agoraPoReportDownloadUrl);
        }
    }

    async checkIsNonScheduledItemsButtonVisible() {
        const hideGTube = this.facilityOrder.facility && this.facilityOrder.facility.facilityAreaOfCares && this.facilityOrder.facility.facilityAreaOfCares.some(faoc => faoc.areaOfCare && faoc.areaOfCare.abrv === "enteral" && faoc.coverageType && faoc.coverageType.abrv === "coveredOnly");
        const hideTrachTube = this.facilityOrder.facility && this.facilityOrder.facility.facilityAreaOfCares && this.facilityOrder.facility.facilityAreaOfCares.some(faoc => faoc.areaOfCare && faoc.areaOfCare.abrv === "respiratory" && faoc.coverageType && faoc.coverageType.abrv === "coveredOnly");
        if (hideGTube && hideTrachTube) {
            return false;
        }

        let params = {
            pageSize: 10000,
            facilityOrderId: this.facilityOrder.id,
            notCurrentTube: true,
            embed: 'FacilityOrderTemplateItems,SubCategory',
            orderBy: "SubCategory",
            orderDirection: "asc",
            inActive: false,
            itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : undefined,
        }

        const response = await this.facilityOrderTemplateService.find(params);
        return response.data.item && response.data.item.some(i => i.facilityOrderTemplateItems.some(x => x.importAction.abrv != 'delete' && x.orderQuantity) && (i.subCategory.abrv == "G-Tube" && !hideGTube) || (i.subCategory.abrv == "Trach-Tube" && !hideTrachTube));
    }

    @action.bound
    async openFacilityOrderNewFlowNonScheduledTubesModal() {
        var result = (await this.facilityOrderTemplateService.importCheck(this.facilityOrder.id, "Send Pick Ticket")).data;
        if (!result.success) {
            return this.rootStore.notificationStore.error(result.errors);
        }
        const recipientData = await this.hasPrimaryRecipient();
        const hasPickTicketRecipient = recipientData.pickTicketRecipientType;
        const hasPrimaryDeliveryRecipient = recipientData.deliveryTicketType;
        if (!hasPickTicketRecipient || !hasPrimaryDeliveryRecipient) {
            const missingName = [!hasPickTicketRecipient && "Packing List", !hasPrimaryDeliveryRecipient && "Delivery Ticket"].filter(Boolean).join(" and ");
            this.openPrimaryRecipientErrorModal(this.openFacilityOrderNewFlowNonScheduledTubesModalExtension, missingName);
            return;
        }

        await this.openFacilityOrderNewFlowNonScheduledTubesModalExtension();
    }

    @action.bound
    async openFacilityOrderNewFlowNonScheduledTubesModalExtension() {
        let params = {
            pageSize: 1,
            facilityOrderId: this.facilityOrder.id,
            notCurrentTube: true,
            hasIncludeNotCurrentTube: true,
            fields: "id",
            itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : undefined,
        }
        const response = await this.facilityOrderTemplateService.find(params);
        const isNotCurrentTubeSaved = response.data.item && response.data.item.some(i => i);
        const isNotCurrentTubeAdded = (await this.facilityOrderService.get(this.facilityOrderId, { fields: "IsNotCurrentTubeAdded" })).data.isNotCurrentTubeAdded;
        const isNonScheduledItemsButtonVisible = await this.checkIsNonScheduledItemsButtonVisible();
        if (!isNotCurrentTubeAdded && isNonScheduledItemsButtonVisible && (this.facilityOrder.facility.isMonthlyTrachTube || isNotCurrentTubeSaved)) {
            return this.facilityOrderNewFlowNonScheduledTubesModal.open({ facilityOrderId: this.facilityOrder.id, sendOrder: async () => await this.sendOrderNewFlow() });
        }
        await this.sendOrderNewFlow();
    }

    @action.bound
    async hasPrimaryRecipient() {
        return (await this.facilityOrderService.hasPrimaryRecipient(this.facilityOrderId)).data;
    }

    @action.bound
    openPrimaryRecipientErrorModal(processAction, missingName) {
        this.primaryRecipientErrorModal.open({
            processAction: processAction,
            missingName: missingName
        });
    }
}

export default PickTicketNewFlowDetailListViewStore;